import { type ChangeEvent, type ReactNode, useCallback, useRef } from 'react'
import cx from 'classnames'
import { Button } from '@/components/Button'
import {
  ModalSheet,
  ModalSheetHeader,
  ModalSheetContent,
  ModalSheetContentTitle,
  ModalSheetContentDescription,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetContentBlock,
} from '@/components/ModalSheet'
import { useFocusTracking } from '@/hooks/focus-hooks'
import styles from './CouponModal.module.css'

export interface CouponModalProps {
  showing: boolean
  isPending: boolean
  onClose: () => unknown
  children?: ReactNode
}

export function CouponModal({
  showing,
  isPending,
  onClose,
  children,
}: CouponModalProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isPending ? undefined : onClose}
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>{children}</ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button label="Close" isDisabled={isPending} onPress={onClose} />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export interface CouponModalCouponsProps {
  count: number
  children?: ReactNode
}

/**
 * The list of the user's coupons, followed by a divider before the add section.
 */
export function CouponModalCoupons({
  count,
  children,
}: CouponModalCouponsProps) {
  return (
    <>
      <ModalSheetContentBlock>
        <ModalSheetContentTitle>
          You have {count === 1 ? 'a coupon' : `${count} coupons`}
        </ModalSheetContentTitle>
        <ModalSheetContentDescription>
          Select one from the list below, or add another one.
        </ModalSheetContentDescription>
      </ModalSheetContentBlock>
      <ModalSheetContentBlock>{children}</ModalSheetContentBlock>

      <Divider />
    </>
  )
}

export function CouponModalNoCoupons() {
  return (
    <>
      <ModalSheetContentBlock>
        <ModalSheetContentTitle>Have a coupon?</ModalSheetContentTitle>
        <ModalSheetContentDescription>
          Enter the code below.
        </ModalSheetContentDescription>
      </ModalSheetContentBlock>
    </>
  )
}

export interface CouponModalCodeInputProps {
  code: string
  onCodeChange: (code: string) => unknown
  hasError: boolean
  isDisabled: boolean
  canAdd: boolean
  isAddPending: boolean
}

export function CouponModalCodeInput({
  code,
  onCodeChange,
  hasError,
  isDisabled,
  canAdd,
  isAddPending,
}: CouponModalCodeInputProps) {
  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onCodeChange(e.target.value),
    [onCodeChange],
  )
  const [focusProps, isFocused] = useFocusTracking()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const focus = useCallback(() => inputRef.current?.focus(), [])
  const testId = 'new-coupon'
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- Mouse-only convenience to focus the inner input, which is itself keyboard-reachable.
    <div
      className={cx(
        styles.inputContainer,
        isFocused && styles.focused,
        hasError && styles.danger,
      )}
      onClick={focus}
    >
      <input
        type="text"
        autoCapitalize="off"
        value={code}
        onChange={onChange}
        disabled={isDisabled}
        ref={inputRef}
        placeholder="Enter your code here."
        data-test={`${testId}-code-input`}
        className={cx(styles.input, code.length > 0 && styles.filled)}
        {...focusProps}
      />
      <Button
        label="Apply"
        type="submit"
        isPending={isAddPending}
        isDisabled={!canAdd || isDisabled}
        testId={`${testId}-apply-button`}
      />
    </div>
  )
}

function Divider() {
  return (
    <ModalSheetContentBlock>
      <div className={styles.divider}>
        <div className={styles.divider}>
          <div className={styles.dividerText}>OR</div>
        </div>
      </div>
    </ModalSheetContentBlock>
  )
}
